import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const WEB3FORMS_KEY = '34b8690d-595c-4a27-8bfc-5180af547bfd';

async function submitToWeb3Forms(formData, subject) {
  const data = new FormData();
  Object.entries({ 
    ...formData, 
    access_key: WEB3FORMS_KEY, 
    subject 
  }).forEach(([key, value]) => data.append(key, value));
  
  const res = await fetch('https://api.web3forms.com/submit', {
    method: 'POST',
    body: data,
  });
  return res.json();
}

function ContactForm({ id, fields, submitLabel = 'Send Message', subject = 'Alercell Website Inquiry' }) {
  const [values, setValues] = useState({});
  const [status, setStatus] = useState('idle');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('submitting');

    // FIRE GA4 EVENT IMMEDIATELY
    try {
      if (window.gtag && typeof window.gtag === 'function') {
        window.gtag('event', 'contact_form_submit', {
          'form_id': id,
          'form_subject': subject,
          'form_location': window.location.pathname,
          'timestamp': new Date().toISOString()
        });
      }
    } catch (err) {
      // Silent fail - don't break form submission if gtag fails
    }

    try {
      const result = await submitToWeb3Forms({ form_type: id, ...values }, subject);
      
      if (result.success) {
        // FIRE SUCCESS EVENT
        try {
          if (window.gtag && typeof window.gtag === 'function') {
            window.gtag('event', 'contact_form_success', {
              'form_id': id,
              'submission_id': result.submission_id || 'unknown'
            });
          }
        } catch (err) {
          // Silent fail
        }

        setStatus('success');
      } else {
        // FIRE ERROR EVENT
        try {
          if (window.gtag && typeof window.gtag === 'function') {
            window.gtag('event', 'contact_form_error', {
              'form_id': id,
              'error_message': result.message || 'form_submission_failed'
            });
          }
        } catch (err) {
          // Silent fail
        }

        setStatus('error');
      }
    } catch (error) {
      // FIRE EXCEPTION EVENT
      try {
        if (window.gtag && typeof window.gtag === 'function') {
          window.gtag('event', 'contact_form_exception', {
            'form_id': id,
            'error_message': error.message || 'unknown_exception'
          });
        }
      } catch (err) {
        // Silent fail
      }

      setStatus('error');
    }
  };

  if (status === 'success') return (
    <div style={{ background: 'var(--accent-cyan-dim)', border: '1px solid var(--accent-cyan-border)', borderRadius: 'var(--radius)', padding: 40, textAlign: 'center', maxWidth: 600 }}>
      <div style={{ fontSize: 32, marginBottom: 12 }}>✓</div>
      <p style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 8 }}>Message received.</p>
      <p style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.6 }}>We'll be in touch within 2 business days at the email address you provided.</p>
    </div>
  );

  return (
    <form onSubmit={handleSubmit} style={{ maxWidth: 600 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        {fields.map((f, i) => (
          <div key={i} className="form-group" style={{ gridColumn: f.wide ? '1 / -1' : 'auto' }}>
            <label className="form-label">{f.label}{f.required ? ' *' : ''}</label>
            {f.type === 'textarea' ? (
              <textarea className="form-textarea" required={f.required} placeholder={f.placeholder} value={values[f.name] || ''} onChange={e => setValues({ ...values, [f.name]: e.target.value })} />
            ) : f.type === 'select' ? (
              <select className="form-select" required={f.required} value={values[f.name] || ''} onChange={e => setValues({ ...values, [f.name]: e.target.value })}>
                <option value="">Select...</option>
                {f.options.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            ) : (
              <input className="form-input" type={f.type || 'text'} required={f.required} placeholder={f.placeholder} value={values[f.name] || ''} onChange={e => setValues({ ...values, [f.name]: e.target.value })} />
            )}
          </div>
        ))}
      </div>
      {status === 'error' && (
        <p style={{ fontSize: 13, color: '#ef4444', marginTop: 16, fontFamily: 'var(--font-mono)' }}>
          Something went wrong. Please try again or email info@alercell.com directly.
        </p>
      )}
      <button
        type="submit"
        className="btn btn-primary"
        disabled={status === 'submitting'}
        style={{ marginTop: 20, padding: '13px 32px', fontSize: 14, opacity: status === 'submitting' ? 0.6 : 1, cursor: status === 'submitting' ? 'wait' : 'pointer' }}
      >
        {status === 'submitting' ? 'Sending…' : submitLabel}
      </button>
    </form>
  );
}

const TABS = ['Clinicians & Lab Directors', 'Investors', 'Partners & BD', 'Media & Press'];

export default function Contact() {
  const [activeTab, setActiveTab] = useState(0);

  return (
    <main>
      {/* Hero */}
      <section className="page-hero">
        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <span className="eyebrow">Get in Touch</span>
          <h1 className="hero-headline" style={{ marginBottom: 24 }}>We respond to every message personally.</h1>
          <p className="body-lg" style={{ maxWidth: 640 }}>
            Whether you're a clinician evaluating our diagnostic platform, a researcher interested in collaboration, an investor, or a journalist covering the epigenetics space — you're in the right place.
          </p>

          {/* Tab Nav */}
          <div style={{ display: 'flex', gap: 8, marginTop: 40, flexWrap: 'wrap' }}>
            {TABS.map((tab, i) => (
              <button key={i} onClick={() => setActiveTab(i)} style={{
                fontFamily: 'var(--font-mono)', fontSize: 11, color: i === activeTab ? '#080D1A' : 'var(--accent-cyan)',
                textTransform: 'uppercase', letterSpacing: '0.1em', padding: '8px 16px',
                border: '1px solid var(--accent-cyan-border)', borderRadius: 20, cursor: 'pointer',
                background: i === activeTab ? 'var(--accent-cyan)' : 'transparent',
                transition: 'all 0.2s',
              }}>{tab}</button>
            ))}
          </div>
        </div>
      </section>

      {/* Forms */}
      <section className="section section-surface">
        <div className="container">
          {activeTab === 0 && (
            <div id="clinical">
              <span className="eyebrow">For Clinicians & Laboratory Directors</span>
              <h2 className="section-headline" style={{ marginBottom: 12 }}>Evaluate the LENA platform for your practice or laboratory.</h2>
              <p className="body-md" style={{ marginBottom: 40, maxWidth: 620 }}>
                If you're a hematologist, oncologist, or clinical laboratory director interested in evaluating the LENA diagnostic platform, ordering information, or discussing clinical implementation, use the form below. A member of our clinical team will respond within 2 business days.
              </p>
              <ContactForm
                id="clinical"
                subject="Alercell — Clinical Inquiry"
                fields={[
                  { name: 'name', label: 'Full Name', required: true, placeholder: 'Dr. Jane Smith' },
                  { name: 'institution', label: 'Institution / Practice', required: true, placeholder: 'Medical center or lab' },
                  { name: 'specialty', label: 'Specialty', type: 'select', required: true, options: ['Hematology', 'Oncology', 'Hematology/Oncology', 'Clinical Laboratory', 'Pathology', 'Internal Medicine', 'Other'] },
                  { name: 'email', label: 'Email', type: 'email', required: true, placeholder: 'your@institution.edu' },
                  { name: 'phone', label: 'Phone', placeholder: '+1 (555) 000-0000' },
                  { name: 'interest', label: 'Primary Interest', type: 'select', options: ['LENA Q51® (Leukemia)', 'LENA S1® (Lung Cancer)', 'Both platforms', 'Research collaboration', 'Other'] },
                  { name: 'message', label: 'Additional Information', type: 'textarea', placeholder: 'Patient population, practice setting, specific questions...', wide: true },
                ]}
                submitLabel="Request Clinical Information"
              />
            </div>
          )}

          {activeTab === 1 && (
            <div id="investor">
              <span className="eyebrow">For Investors</span>
              <h2 className="section-headline" style={{ marginBottom: 12 }}>Alercell is currently in a Series A financing round.</h2>
              <p className="body-md" style={{ marginBottom: 20, maxWidth: 620 }}>
                For investor inquiries, please visit our Investor Relations page for context on the platform and the opportunity, then use the form there to request materials. If you've already visited and are ready to connect directly, use the form below.
              </p>
              <p style={{ marginBottom: 32 }}>
                <Link to="/investors" className="btn btn-text">Visit Investor Relations</Link>
              </p>
              <ContactForm
                id="investor"
                subject="Alercell — Investor Inquiry"
                fields={[
                  { name: 'name', label: 'Full Name', required: true, placeholder: 'Your name' },
                  { name: 'firm', label: 'Firm / Organization', placeholder: 'Fund or organization' },
                  { name: 'email', label: 'Email', type: 'email', required: true, placeholder: 'your@fund.com' },
                  { name: 'message', label: 'Message', type: 'textarea', wide: true, placeholder: 'Investment focus, stage, any questions...' },
                ]}
                submitLabel="Get in Touch"
              />
            </div>
          )}

          {activeTab === 2 && (
            <div id="partner">
              <span className="eyebrow">For Partners & Business Development</span>
              <h2 className="section-headline" style={{ marginBottom: 12 }}>Explore clinical, research, or commercial collaboration with Alercell.</h2>
              <p className="body-md" style={{ marginBottom: 40, maxWidth: 620 }}>
                We are actively developing partnerships with academic medical centers, health systems, clinical reference laboratories, pharmaceutical companies, and research institutions. If you are interested in clinical validation collaboration, technology licensing, co-development, or commercial distribution, we want to hear from you.
              </p>
              <ContactForm
                id="partner"
                subject="Alercell — Partnership Inquiry"
                fields={[
                  { name: 'name', label: 'Full Name', required: true, placeholder: 'Your name' },
                  { name: 'organization', label: 'Organization', required: true, placeholder: 'Your institution or company' },
                  { name: 'email', label: 'Email', type: 'email', required: true, placeholder: 'your@org.com' },
                  { name: 'phone', label: 'Phone', placeholder: '+1 (555) 000-0000' },
                  { name: 'type', label: 'Partnership Type', type: 'select', options: ['Clinical validation', 'Technology licensing', 'Co-development', 'Commercial distribution', 'Research collaboration', 'Other'] },
                  { name: 'message', label: 'Describe Your Interest', type: 'textarea', required: true, wide: true, placeholder: 'Organization background, proposed collaboration, timeline...' },
                ]}
                submitLabel="Submit Partnership Inquiry"
              />
            </div>
          )}

          {activeTab === 3 && (
            <div id="media">
              <span className="eyebrow">For Media & Press</span>
              <h2 className="section-headline" style={{ marginBottom: 12 }}>Press inquiries, interview requests, and editorial fact-checking.</h2>
              <p className="body-md" style={{ marginBottom: 40, maxWidth: 620 }}>
                For journalists, editors, and podcast hosts covering the epigenetics, AI diagnostics, or oncology space. We are available for background conversations, on-record quotes, and interview requests. Please indicate your publication and deadline in your message.
              </p>
              <ContactForm
                id="media"
                subject="Alercell — Media Inquiry"
                fields={[
                  { name: 'name', label: 'Full Name', required: true, placeholder: 'Your name' },
                  { name: 'publication', label: 'Publication / Outlet', required: true, placeholder: 'Where you publish' },
                  { name: 'email', label: 'Email', type: 'email', required: true, placeholder: 'your@publication.com' },
                  { name: 'deadline', label: 'Deadline', placeholder: 'e.g., March 25, 2026' },
                  { name: 'message', label: 'Request Details', type: 'textarea', required: true, wide: true, placeholder: 'Story angle, specific questions, type of request (interview, quote, background)...' },
                ]}
                submitLabel="Submit Press Inquiry"
              />
            </div>
          )}
        </div>
      </section>

      {/* Direct Contact */}
      <section className="section" style={{ background: 'var(--bg-primary)' }}>
        <div className="container">
          <span className="eyebrow">Direct Contact</span>
          <h2 className="section-headline" style={{ marginBottom: 32 }}>Our team is in Bozeman, Montana.</h2>
          <div className="stat-grid">
            {[
              { label: 'General Inquiries', value: 'info@alercell.com', type: 'email' },
              { label: 'Investor Relations', value: 'fscheer@alercell.com', type: 'email' },
              { label: 'Headquarters', value: 'Bozeman, Montana\nUnited States', type: 'text' },
              { label: 'Substack', value: 'fscheer.substack.com', type: 'link', href: 'https://fscheer.substack.com' },
            ].map((c, i) => (
              <div key={i} style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 28 }}>
                <p style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--accent-cyan)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 10 }}>{c.label}</p>
                {c.type === 'email' ? (
                  <a href={`mailto:${c.value}`} style={{ fontFamily: 'var(--font-display)', fontSize: 15, fontWeight: 500, color: 'var(--text-primary)', textDecoration: 'none' }}>{c.value}</a>
                ) : c.type === 'link' ? (
                  <a href={c.href} target="_blank" rel="noopener noreferrer" style={{ fontFamily: 'var(--font-display)', fontSize: 15, fontWeight: 500, color: 'var(--accent-cyan)', textDecoration: 'none' }}>{c.value} ↗</a>
                ) : (
                  <p style={{ fontFamily: 'var(--font-display)', fontSize: 15, fontWeight: 500, color: 'var(--text-primary)', whiteSpace: 'pre-line' }}>{c.value}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
